
import Vue, { PropType } from 'vue';

// Types and services
import {
    CandidateProfileScore,
    CandidateProfileScorer,
} from '~/utils/candidate/CandidateProfileScorer';
import { Widget } from '~/services/types/organization';
import BackgroundProcessesService from '~/services/BackgroundProcessesService';
import { ProfileTab } from './ProfileTabs.vue';
import { SchoolVerification, SchoolVerificationState } from '~/services/SchoolService';
import { CandidateStudyProgram } from '~/services/CSPService';
import { Dictionary } from 'vue-router/types/router';
import { ProfileImportResult } from '~/services/types/import';
import { User } from '~/services/types/identity';
import { Candidate, WorkAuthorization } from '~/services/types/candidate';

// Components
import ImportSummaryForm from '~/components/profile/forms/ImportSummaryForm.vue';
import ProfileWrapper from './ProfileWrapper.vue';
import JobPreferences from '~/components/profile/cards/JobPreferences.vue';
import Membership from '~/components/profile/cards/Membership.vue';
import ProfileSkeleton from './ProfileSkeleton.vue';
import ProfileWidgetCard from './components/ProfileWidgetCard.vue';
import WidgetModule from '~/components/smart/WidgetModule.vue';
import EmptyProfileInfo from '~/components/profile/cards/EmptyProfileInfo.vue';
import Languages from '~/components/profile/cards/Languages.vue';
import Links from '~/components/profile/cards/Links.vue';
import Skills from '~/components/profile/cards/Skills.vue';
import StudyPrograms from '~/components/profile/cards/StudyPrograms.vue';
import Summary from '~/components/profile/cards/Summary.vue';
import WorkExperience from '~/components/profile/cards/WorkExperience.vue';

// Utils
import { showLastImport } from '~/utils/candidate/ProfileImportHelper';

// Constants
const MIDDLE_WIDGET_ID = '9fefd759-b247-4829-9e53-0e93c5a74b54';

export default Vue.extend({
    name: 'Profile',
    components: {
        ProfileWrapper,
        Summary,
        Languages,
        WorkExperience,
        Skills,
        JobPreferences,
        Links,
        StudyPrograms,
        Membership,
        ProfileSkeleton,
        ProfileWidgetCard,
        WidgetModule,
        EmptyProfileInfo,
        ImportSummaryForm,
    },
    props: {
        tabs: {
            type: Array as PropType<ProfileTab[]>,
            required: false,
        },
    },
    data() {
        return {};
    },
    computed: {
        showLastImport(): boolean {
            return showLastImport(this.lastProfileImport);
        },
        lastProfileImport(): ProfileImportResult | null {
            return this.$store.getters['profileModule/getLastProfileImport']();
        },
        user(): User | null {
            return this.$store.getters['profileModule/getUser']();
        },
        candidate(): Candidate | null {
            return this.$store.getters['profileModule/getCandidate']();
        },
        workAuthorizations(): WorkAuthorization[] | null {
            return this.$store.getters['profileModule/getWorkAuthorizations']();
        },
        profileScore(): CandidateProfileScore | null {
            if (this.candidate && this.user && this.workAuthorizations)
                return new CandidateProfileScorer(
                    this.candidate,
                    this.user,
                    this.workAuthorizations
                ).getProfileScore();
            return null;
        },
        isLoading(): boolean {
            return !this.user || !this.candidate || !this.workAuthorizations || !this.widgets;
        },
        widgets(): Widget[] {
            return (this.$store.getters['widgetModule/getAllWidgets']() as Widget[] | null) || [];
        },
        bottomWidgets(): Widget[] {
            return this.widgets
                ? this.widgets.filter(widget => widget.id !== MIDDLE_WIDGET_ID)
                : [];
        },
        middleWidget(): Widget | null {
            return this.widgets
                ? this.widgets.find(widget => widget.id === MIDDLE_WIDGET_ID) || null
                : null;
        },
        studyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getSortedStudyPrograms']();
        },
        schoolVerifications(): SchoolVerification[] {
            return this.$interfaces.university.connection.get({ reactive: true }).data || [];
        },
        queryParams(): Dictionary<string | string[]> {
            return this.$route.query;
        },
    },
    methods: {
        async loadSchoolVerifications(startPoll = false): Promise<SchoolVerification[]> {
            const verifications: SchoolVerification[] =
                (
                    await this.$interfaces.university.connection.get({
                        reactive: true,
                        forceUpdate: true,
                    })
                ).data || [];

            if (startPoll) {
                this.startVerificationPoll(verifications);
            }

            return verifications;
        },
        startVerificationPoll(verifications: SchoolVerification[]): void {
            if (
                !verifications.length ||
                verifications.every(x => x.state === SchoolVerificationState.verified)
            ) {
                return;
            }

            BackgroundProcessesService.startVerificationPing();
        },
        refresh() {
            this.loadSchoolVerifications(true);
        },
        openProfileDetailsModal() {
            window.dispatchEvent(new CustomEvent('candidate:profile:details'));
        },
        openProfileImportModal(state: string) {
            const event = new CustomEvent('profileimport:modal:show', {
                detail: state,
            });
            window.dispatchEvent(event);
        },
        scrollTo(elementId: string | string[]): void {
            if (elementId) {
                setTimeout(() => {
                    this.$scrollTo(`#${elementId}`, { offset: -200 });
                }, 500);
            }
        },
    },
    watch: {
        schoolVerifications(newValue): void {
            this.$store.commit('profileModule/setSchoolVerifications', newValue);
        },
        queryParams(newValue): void {
            this.scrollTo(newValue.scrollTo);
        },
    },
    mounted() {
        this.refresh();
        window.addEventListener('candidate:profile:update', this.refresh);
        this.scrollTo(this.$route.query.scrollTo);
    },
    beforeDestroy() {
        window.removeEventListener('candidate:profile:update', this.refresh);
    },
});
